import { useEffect, useLayoutEffect } from "react";
import {
  changeCoordinates,
  setLayersVisible,
  setZoom,
} from "../services/reducers/map";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector } from "./useAppSelector";
import { useAppDispatch } from "./useAppDispatch";

interface ExcludedPathConfig {
  path: string;
  exact: boolean;
}

const useSyncMapSearchParams = (
  excludedPaths: ExcludedPathConfig[] = [],
): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { visibleLayersId, coordinates, zoom } = useAppSelector(
    (store) => store.map,
  );

  const location = useLocation();
  const dispatch = useAppDispatch();

  const isExcluded = excludedPaths.some((config) =>
    config.exact
      ? location.pathname === config.path
      : location.pathname.includes(config.path),
  );

  useLayoutEffect(() => {
    if (isExcluded) return;

    const layersParam = searchParams.get("lId");
    if (layersParam) {
      const layersArray = layersParam.split(",").map((item) => +item);
      if (JSON.stringify(layersArray) !== JSON.stringify(visibleLayersId))
        dispatch(setLayersVisible(layersArray));
    }

    const coordinatesParam = searchParams.get("ll");
    if (coordinatesParam) {
      const coordsArray = coordinatesParam.split(",").map(Number);
      if (coordsArray.length === 2) {
        dispatch(
          changeCoordinates({
            latitude: coordsArray[0],
            longitude: coordsArray[1],
          }),
        );
      }
    }

    const zoomParam = searchParams.get("z");
    if (zoomParam) {
      dispatch(setZoom({ zoom: +zoomParam }));
    }
  }, []);

  useEffect(() => {
    if (isExcluded) return;

    const newSearchParams = new URLSearchParams();

    const excludedKeys = ["lId", "ll", "z"];
    searchParams.forEach((paramValue, paramKey) => {
      if (!excludedKeys.includes(paramKey)) {
        newSearchParams.set(paramKey, paramValue);
      }
    });

    newSearchParams.set("lId", visibleLayersId.join(","));
    newSearchParams.set(
      "ll",
      `${coordinates.latitude},${coordinates.longitude}`,
    );
    newSearchParams.set("z", String(zoom));

    const newQueryString = newSearchParams.toString();
    const currentQueryString = location.search.startsWith("?")
      ? location.search.slice(1)
      : location.search;

    if (newQueryString !== currentQueryString) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [
    visibleLayersId.join(","),
    coordinates.latitude,
    coordinates.longitude,
    zoom,
    location.pathname,
    location.search,
    searchParams,
  ]);
};

export default useSyncMapSearchParams;
